/**
 * Project model upload flow (Wave 2): parse the .ifc locally in the Wave 1
 * worker pipeline while the raw file uploads, then push the extracted
 * evidence envelope and Fragments binary to the server.
 *
 *   POST /api/projects/{id}/models  (raw .ifc, multipart)    ─┐ in parallel
 *   web worker: parse → ModelSummary + evidence + Fragments   ─┘
 *   PUT /api/models/{model_id}/ingestion ─┐ in parallel (need model_id)
 *   PUT /api/models/{model_id}/fragments ─┘
 *
 * `putModelIngestion` (not the legacy, evidence-blind `putModelSummary`) is
 * used here so the browser-originated evidence reaches `evaluate_quality`
 * server-side — otherwise a browser upload would be stuck on the
 * legacy/pending quality path forever, and `enforce` mode would incorrectly
 * block otherwise-valid models (ENG-17381 Revit IFC readiness; see the
 * "browser evidence" note in `docs/CONTRACT-model-summary.md`).
 */
import { parseIfcFile } from "../viewer/ifc/parseIfc";
import type { ModelSummary, ParseProgress } from "../viewer/ifc/types";
import { putModelFragments, putModelIngestion, uploadModel } from "./client";

export type UploadPhase = ParsePhase | "uploading" | "saving";
type ParsePhase = ParseProgress["phase"];

export interface UploadProgress {
  phase: UploadPhase;
  /** Overall progress in [0, 1] across parse + save. */
  ratio: number;
}

export interface UploadedModel {
  modelId: string;
  fileName: string;
  summary: ModelSummary;
  /**
   * Fragments binary, reusable for the viewer without re-parsing — `null`
   * when the worker's 3D conversion failed (see `ParsedIfc.fragments`). The
   * upload still succeeds in that case (the summary is the load-bearing
   * artifact); no fragments are PUT to the server and no 3D view is
   * available downstream until Task 2e/UX follow-up addresses this.
   */
  fragments: ArrayBuffer | null;
}

/**
 * Run the full upload pipeline for one .ifc file. Rejects with the first
 * failure (parse error or any HTTP error from the three API calls).
 */
export async function uploadIfcModel(
  projectId: string,
  file: File,
  onProgress?: (progress: UploadProgress) => void,
): Promise<UploadedModel> {
  onProgress?.({ phase: "uploading", ratio: 0 });

  const [created, parsed] = await Promise.all([
    uploadModel(projectId, file),
    parseIfcFile(file, (p) =>
      // Parse is the bulk of the work: map it onto [0, 0.9].
      onProgress?.({ phase: p.phase, ratio: p.ratio * 0.9 }),
    ),
  ]);

  onProgress?.({ phase: "saving", ratio: 0.9 });
  const puts = [
    putModelIngestion(created.model_id, {
      version: "1",
      summary: parsed.summary,
      evidence: parsed.evidence,
    }),
  ];
  // 3D conversion may have failed (parsed.fragments === null); there is
  // nothing to PUT in that case, but the summary/evidence are still saved
  // above (2D-only operation — readiness, wizard, plan view — is a
  // genuinely supported mode; see `evidence.viewer.threeD`).
  if (parsed.fragments !== null) {
    puts.push(putModelFragments(created.model_id, parsed.fragments));
  }
  await Promise.all(puts);
  onProgress?.({ phase: "saving", ratio: 1 });

  return {
    modelId: created.model_id,
    fileName: file.name,
    summary: parsed.summary,
    fragments: parsed.fragments,
  };
}
